import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '@hostfully/ui-components';
import { ButtonContainer, InputContainer } from './Register.styles';
import RegisterTermsNotice from './RegisterTermsNotice';
import { UserRegistrationType } from '@/services/register.types';
import FormFieldset from '@/components/forms/form/FormFieldset';
import InputField from '@/components/forms/InputField';
import PasswordField from '@/components/forms/PasswordField';
import PhoneNumberField from '@/components/forms/PhoneNumberField';
import { usePasswordStrength } from '@/hooks/usePasswordStrength';
import useIsMobileScreen from '@/hooks/useIsMobileScreen';

interface RegisterFormProps {
  clearErrors: () => void;
  errorMessage?: string;
  isSubmitting: boolean;
  registrationType: UserRegistrationType;
}

const RegisterForm = ({
  clearErrors,
  errorMessage,
  isSubmitting,
  registrationType,
}: RegisterFormProps) => {
  const { t } = useTranslation();
  const { trigger, getValues } = useFormContext();
  const { email, password, confirmPassword, phoneNumber } = useWatch();
  const navigate = useNavigate();
  const { setPasswordStrength } = usePasswordStrength();
  const isMobile = useIsMobileScreen();

  const isInvitationMode = registrationType === UserRegistrationType.INVITATION;

  useEffect(() => {
    clearErrors();
  }, [clearErrors, email, password, confirmPassword, phoneNumber]);

  useEffect(() => {
    if (getValues('confirmPassword')) {
      trigger('confirmPassword');
    }
  }, [password, trigger, getValues]);

  return (
    <FormFieldset disabled={isSubmitting}>
      <InputContainer>
        <InputField
          disabled={isInvitationMode}
          label={t('pageRegister.email')}
          name="email"
          placeholder={t('pageRegister.email')}
          type="email"
        />
      </InputContainer>
      {isInvitationMode && (
        <InputContainer>
          <PhoneNumberField
            label={t('pageRegister.phoneNumber')}
            name="phoneNumber"
          />
        </InputContainer>
      )}
      <InputContainer>
        <PasswordField
          label={t('pageRegister.password')}
          name="password"
          placeholder={t('pageRegister.password')}
          enableStrengthCheck
          onStrengthChange={setPasswordStrength}
        />
      </InputContainer>
      <InputContainer>
        <PasswordField
          label={t('pageRegister.confirmPassword')}
          name="confirmPassword"
          placeholder={t('pageRegister.confirmPassword')}
        />
      </InputContainer>
      <RegisterTermsNotice />
      <ButtonContainer>
        {isInvitationMode && (
          <Button
            data-testid="login-link-button"
            variant="textPrimary"
            onClick={() => navigate('/login')}
            disabled={isSubmitting}
          >
            {t('pageRegister.alreadyHaveAccount')}
          </Button>
        )}
        <Button
          data-testid="submit-button"
          disabled={!!errorMessage || isSubmitting}
          size="large"
          type="submit"
          fullWidth={isMobile}
        >
          {t('pageRegister.signUp')}
        </Button>
      </ButtonContainer>
      {!!errorMessage && (
        <p className="error-message" data-testid="error-message">
          {errorMessage}
        </p>
      )}
    </FormFieldset>
  );
};

export default RegisterForm;
